import {Component, HostListener} from '@angular/core';
import {CanDeactivateFn} from '@angular/router';
import {ChoiceGroupVM} from '../produtos-v2.models';
import {TelaEditorAdicionalGlobalV2Component} from '../tela-editor-adicional-global-v2/tela-editor-adicional-global-v2.component';

export interface RegraProdutoV3 {
  id: number;
  nome: string;
  tipo: string;
  obrigatorio: boolean;
  qtdMinima: number;
  qtdMaxima: number | null;
  pendente?: boolean;
  salvando?: boolean;
  erro?: string;
  versao?: number;
  timer?: ReturnType<typeof setTimeout>;
  gravacao?: Promise<void>;
}

@Component({
  selector: 'app-tela-editor-adicional-v3',
  templateUrl: './tela-editor-adicional-v3.component.html',
  styleUrls: ['../tela-editor-adicional-global-v2/tela-editor-adicional-global-v2.component.scss',
    './tela-editor-adicional-v3.component.scss']
})
export class TelaEditorAdicionalV3Component extends TelaEditorAdicionalGlobalV2Component {
  aba: 'compartilhados' | 'produtos' = 'compartilhados';
  avisoCompartilhadoFechado = false;
  regras: RegraProdutoV3[] = [];
  selecionado: RegraProdutoV3 | null = null;
  pesquisa = '';
  carregandoRegras = false;
  erroRegras = '';
  removendo = false;
  remover: RegraProdutoV3 | null = null;
  private leitura = 0;

  override async load(): Promise<void> {
    this.avisoCompartilhadoFechado = false;
    await super.load();
    if (this.group) await this.carregueRegras();
  }

  async carregueRegras(): Promise<void> {
    if (!this.group) return;
    const leitura = ++this.leitura;
    const id = this.group.id;
    this.carregandoRegras = true;
    this.erroRegras = '';
    try {
      const linhas = await this.facade.carregueRegrasProdutos(id!);
      if (leitura !== this.leitura || id !== this.group?.id) return;
      const atuais = new Map(this.regras.map(regra => [regra.id, regra]));
      this.regras = linhas.map(linha => {
        const atual = atuais.get(linha.id);
        return atual?.pendente || atual?.salvando ? atual : {
          ...linha, obrigatorio: !!Number(linha.obrigatorio), qtdMinima: Number(linha.qtdMinima || 0),
          qtdMaxima: linha.qtdMaxima == null ? null : Number(linha.qtdMaxima)
        };
      });
      const selecionadoId = this.selecionado?.id || Number(this.route.snapshot.queryParams['produto']) || this.editor?.raw?.id;
      this.selecionado = this.regras.find(r => r.id === selecionadoId) || this.regras[0] || null;
      this.sharedProductNames = this.regras.map(r => r.nome);
      this.idsVinculados = this.regras.map(r => r.id);
      this.group.sharedCount = this.regras.length;
      this.group.shared = this.regras.length > 1 || !!this.group.raw?.compartilhado || this.group.shared;
      const draft = this.editorAdicional?.groupDraft;
      if (draft) {
        draft.shared = this.group.shared;
        draft.sharedCount = this.regras.length;
        draft.sharedProductNames = [...this.sharedProductNames];
        if (draft.raw) draft.raw.compartilhado = draft.shared;
      }
      if (this.libraryItem) {
        this.libraryItem.shared = this.group.shared;
        this.libraryItem.sharedCount = this.regras.length;
        this.libraryItem.sharedProductNames = [...this.sharedProductNames];
      }
    } catch (erro: any) {
      this.erroRegras = erro?.message || 'Não foi possível carregar as regras dos produtos.';
    } finally {
      if (leitura === this.leitura) this.carregandoRegras = false;
    }
  }

  protected override async recarregueVinculos(): Promise<void> {
    await super.recarregueVinculos();
    await this.carregueRegras();
  }

  override onGroupSaved(group: ChoiceGroupVM): void {
    super.onGroupSaved(group);
    void this.carregueRegras();
  }

  get regrasFiltradas(): RegraProdutoV3[] {
    const texto = this.pesquisa.trim().toLocaleLowerCase();
    return this.regras.filter(r => r.nome.toLocaleLowerCase().includes(texto));
  }

  get opcoesPreview() {
    return (this.editorAdicional?.groupDraft || this.group)?.options.filter(o => o.active !== false) || [];
  }

  get grupoKit(): boolean { return this.group?.type === 'kit'; }

  override get temAlteracaoPendente(): boolean {
    return super.temAlteracaoPendente || this.regras.some(r => r.pendente || r.salvando);
  }

  override get rotuloSalvamento(): string {
    if (this.regras.some(r => r.salvando)) return 'Salvando...';
    if (this.regras.some(r => r.pendente)) return 'Alterações não salvas';
    return super.rotuloSalvamento;
  }

  override salvarAgora(): void { void this.salveTudo(); }

  @HostListener('window:keydown', ['$event'])
  atalhoSalvar(evento: KeyboardEvent): void {
    if (this.aba === 'produtos' && (evento.ctrlKey || evento.metaKey) && evento.key === 's') {
      evento.preventDefault();
      if (evento.shiftKey) void this.salvarEFechar();
      else this.salvarAgora();
    }
  }

  altereRegra(regra: RegraProdutoV3, campo: 'obrigatorio' | 'minimo' | 'maximo'): void {
    if (!this.canEditStructure || this.grupoKit) return;
    this.selecionado = regra;
    if (campo === 'obrigatorio') regra.qtdMinima = regra.obrigatorio ? Math.max(1, regra.qtdMinima || 0) : 0;
    if (campo === 'minimo' && regra.qtdMinima != null) regra.obrigatorio = regra.qtdMinima > 0;
    regra.versao = (regra.versao || 0) + 1;
    regra.pendente = true;
    regra.erro = this.valideRegra(regra);
    if (regra.timer) clearTimeout(regra.timer);
    if (!regra.erro) regra.timer = setTimeout(() => { regra.timer = undefined; void this.salveRegra(regra); }, 800);
  }

  valideRegra(regra: RegraProdutoV3): string {
    if (!Number.isInteger(regra.qtdMinima) || regra.qtdMinima < 0 ||
        (regra.qtdMaxima != null && (!Number.isInteger(regra.qtdMaxima) || regra.qtdMaxima < Math.max(1, regra.qtdMinima))))
      return 'Informe quantidades inteiras; o máximo deve ser maior ou igual ao mínimo.';
    if (regra.obrigatorio && regra.qtdMinima < 1) return 'Um adicional obrigatório precisa de mínimo 1.';
    if (regra.tipo === 'escolha-simples' && (regra.qtdMaxima !== 1 || regra.qtdMinima > 1))
      return 'Escolha única permite no máximo uma opção.';
    return '';
  }

  async salveRegra(regra: RegraProdutoV3): Promise<void> {
    if (regra.timer) { clearTimeout(regra.timer); regra.timer = undefined; }
    if (regra.gravacao) return regra.gravacao;
    if (!regra.pendente || !this.canEditStructure) return;
    regra.erro = this.valideRegra(regra);
    if (regra.erro) return;
    const versao = regra.versao;
    const dados = {obrigatorio: regra.obrigatorio, qtdMinima: regra.qtdMinima, qtdMaxima: regra.qtdMaxima};
    regra.salvando = true;
    regra.gravacao = (async () => {
      try {
        await this.facade.salveRegrasProduto(this.empresa.catalogo, this.group!.id!, regra.id, dados);
        if (regra.versao === versao) { regra.pendente = false; regra.erro = ''; }
      } catch (erro: any) {
        regra.erro = erro?.message || 'Não foi possível salvar. Tente novamente.';
      } finally { regra.salvando = false; }
    })();
    await regra.gravacao;
    regra.gravacao = undefined;
  }

  async salveTudo(): Promise<boolean> {
    if (this.editorAdicional?.isDirty) await this.editorAdicional.onSave();
    for (const regra of this.regras) await this.salveRegra(regra);
    return !this.temAlteracaoPendente && !this.editorAdicional?.isDirty;
  }

  async salvarEFechar(): Promise<void> {
    if (await this.salveTudo()) super.goBack();
  }

  async confirmeRemocao(): Promise<void> {
    if (!this.remover || !this.canEditStructure || !this.group || this.removendo) return;
    if (!await this.salveTudo()) return;
    this.removendo = true;
    try {
      await this.facade.removaGrupo({id: this.remover.id}, this.empresa.catalogo, this.group);
      this.remover = null;
      await this.carregueRegras();
      // O editor compartilhado precisa de um vínculo válido como contexto.
      if (!this.regras.length) { super.goBack(); return; }
      if (!this.regras.some(r => r.id === this.editor?.raw?.id)) {
        const templates = await this.facade.carregueTemplates(this.empresa.catalogo);
        this.editor = await this.facade.carregueProduto(this.empresa, this.regras[0].id, templates);
        this.group = this.editor.groups.find(g => String(g.id) === String(this.group!.id)) || null;
      }
      this.notificationService.showSuccess('Adicional removido deste produto.');
    } catch (erro: any) { this.notificationService.showError(erro?.message || 'Não foi possível remover o vínculo.'); }
    finally { this.removendo = false; }
  }

  abraProduto(): void {
    if (this.selecionado) void this.router.navigate(['/admin/produtos-v2'], {
      queryParams: {produtoDrawer: this.selecionado.id, drawerTab: 'adicionais'}
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  protejaAlteracoes(evento: BeforeUnloadEvent): void {
    if (this.temAlteracaoPendente) { evento.preventDefault(); evento.returnValue = ''; }
  }

  override ngOnDestroy(): void {
    ++this.leitura;
    this.regras.forEach(r => { if (r.timer) clearTimeout(r.timer); });
    super.ngOnDestroy();
  }
}

export const salveAntesDeSairAdicionalV3: CanDeactivateFn<TelaEditorAdicionalV3Component> = tela => tela.salveTudo();
